<template>
  <layout>
    <div
      class="relative space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 md:w-0 md:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <h1 class="text-gray-900 text-xl font-medium">
            {{ t("admin.settings.recurring_tasks.title") }}
          </h1>
        </div>
        <template v-if="canCreateRecurringTasks">
          <primary-button
            class="hidden sm:flex"
            :label="t('admin.settings.recurring_tasks.add_task')"
            @click="handleOpenAddRecurringTasks"
          />
          <PlusIcon
            class="w-10 text-gray-900 sm:hidden -mr-1"
            @click="handleOpenAddRecurringTasks"
          />
        </template>
      </div>
      <div
        class="flex items-center space-x-2 pt-3 overflow-x-auto hide-scrollbar text-sm"
      >
        <div class="relative rounded-sm">
          <div
            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
          >
            <MagnifyingGlassIcon
              class="h-5 w-5 text-gray-800"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            name="search"
            id="search"
            :value="filters?.query"
            class="bg-gray-100 text-sm placeholder:text-gray-800 py-2 block rounded-sm border-transparent pl-10 focus:border-transparent focus:ring-0"
            :placeholder="t('components.buttons.search')"
            v-debounce:500ms="handleSearch"
          />
        </div>
        <HierarchySelector
          :hierarchy="spaceHierarchy"
          :label="t('admin.search.filters.space')"
          :search-placeholder="t('components.inputs.searchLocations')"
          allowTenantSelection
          multiple
          @change="handleLocationsSelected"
          :value="filters.spaces ?? []"
          cascadeCheck
        />
        <HierarchySelector
          :hierarchy="assetHierarchy"
          :label="t('admin.search.filters.asset')"
          :search-placeholder="t('components.inputs.search')"
          allowTenantSelection
          multiple
          @change="handleAssetsSelected"
          :value="filters.assets ?? []"
          cascadeCheck
        />
        <DropdownSelector
          @change="handleCategoriesSelected"
          :value="filters.categories ?? []"
          :options="categories"
          :label="t('admin.search.filters.category')"
          multiple
        />
        <users-filter
          @change="handleUsersSelected"
          :value="filters.users ?? []"
          :tenant-ids="activeTenantsForUsersFilter"
        />
        <DropdownSelector
          @change="handlePrioritySelected"
          :value="filters?.priorities"
          :options="priorities"
          :label="t('admin.search.filters.priority')"
          multiple
        />
        <DropdownSelector
          @change="handleIntervalSelected"
          :value="filters?.intervals"
          :options="intervals"
          :label="t('admin.settings.recurring_tasks.recurrence')"
          multiple
        />
      </div>
    </div>
    <div
      class="dynamic-height overflow-auto"
      @scroll="loadTasksWhenBottomIsReached"
    >
      <div v-if="type !== 'xs'">
        <table
          class="min-w-full table-fixed border-0 border-separate border-spacing-0"
        >
          <thead class="sticky top-0 bg-gray-50">
            <tr>
              <th
                scope="col"
                class="w-[15%] py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6 border-b border-gray-200"
              >
                <sort-button
                  :label="t('admin.settings.recurring_tasks.space')"
                  column="space"
                  @sort="handleSortInner"
                  :value="getSortValue('space')"
                />
              </th>
              <th
                scope="col"
                class="w-[35%] px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
              >
                <sort-button
                  :label="t('admin.settings.recurring_tasks.description')"
                  column="description"
                  @sort="handleSortInner"
                  :value="getSortValue('description')"
                />
              </th>
              <th
                scope="col"
                class="w-[10%] hidden sm:table-cell px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
              >
                <sort-button
                  :label="t('admin.settings.recurring_tasks.category')"
                  column="category"
                  @sort="handleSortInner"
                  :value="getSortValue('category')"
                />
              </th>
              <th
                scope="col"
                class="w-[10%] hidden sm:table-cell px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
              >
                <sort-button
                  :label="t('admin.settings.recurring_tasks.priority')"
                  column="priority"
                  @sort="handleSortInner"
                  :value="getSortValue('priority')"
                />
              </th>
              <th
                scope="col"
                class="w-[10%] hidden sm:table-cell px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
              >
                <sort-button
                  :label="t('admin.settings.recurring_tasks.assignee')"
                  column="assignee"
                  @sort="handleSortInner"
                  :value="getSortValue('assignee')"
                />
              </th>
              <th
                scope="col"
                class="w-[20%] hidden sm:table-cell px-3 py-3.5 text-left text-sm font-medium text-gray-900 border-b border-gray-200"
              >
                {{ t("admin.settings.recurring_tasks.recurrence") }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr
              v-for="recurringTask in recurringTasks"
              :key="recurringTask.id"
              @click="handleRecurringTaskClicked(recurringTask.id)"
              class="hover:cursor-pointer hover:bg-gray-50"
            >
              <td
                class="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6 border-b border-gray-200"
              >
                <div class="text-gray-900 font-medium">
                  {{
                    recurringTask.space
                      ? recurringTask.space?.name
                      : t("admin.tasks.no_space")
                  }}
                </div>
                <div class="text-gray-500 font-normal">
                  {{ recurringTask.tenant?.name }}
                </div>
              </td>
              <td
                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
              >
                <div
                  :title="recurringTask?.description"
                  class="max-w-sm overflow-hidden text-ellipsis"
                >
                  {{ recurringTask?.description }}
                </div>
              </td>
              <td
                class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
              >
                <category
                  v-if="recurringTask.category"
                  :category="recurringTask.category"
                  class="max-w-fit"
                />
              </td>
              <td
                class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
              >
                <priority
                  :priority="recurringTask.priority"
                  class="max-w-fit"
                />
              </td>
              <td
                class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
              >
                <div class="flex items-center space-x-2">
                  <assignee
                    v-for="assignee in recurringTask.assignees"
                    :key="assignee.id"
                    :assignee="assignee"
                  />
                </div>
              </td>
              <td
                class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium border-b border-gray-200"
                :title="capitalise(recurringTask.rrule.text)"
              >
                <div>{{ capitalise(recurringTask.rrule.text) }}</div>
              </td>
            </tr>
          </tbody>
          <tbody class="divide-y divide-gray-200 bg-white" v-if="isSearching">
            <tr v-for="row in Math.floor(Math.random() * 10) + 1" :key="row">
              <td class="py-3 pl-4 pr-3 sm:pl-6 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
              <td class="px-3 py-4 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
              <td class="px-3 py-4 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
              <td class="px-3 py-4 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
              <td class="px-3 py-4 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
              <td class="px-3 py-4 border-b border-gray-200">
                <div
                  class="h-5 w-2/3 bg-gray-200 animate-pulse rounded-xs"
                ></div>
              </td>
            </tr>
          </tbody>
        </table>

        <NoResults
          image="/images/empty/tasks.svg"
          :title="t('components.empty_states.recurring_tasks.title')"
          :description="
            t('components.empty_states.recurring_tasks.description')
          "
          :button-label="t('admin.settings.recurring_tasks.add_task')"
          :hideButton="() => !hasPermission(permissions.createRecurringTask)"
          @click="handleOpenAddRecurringTasks"
          v-if="recurringTasks.length === 0 && !isSearching && !hasFilters"
        />
        <NoResults
          @click="clearFilters"
          v-else-if="recurringTasks.length === 0 && !isSearching && hasFilters"
        />
      </div>
      <div v-else>
        <div
          v-if="recurringTasks.length === 0 && !isSearching && !hasFilters"
          class="flex justify-center mt-24"
        >
          <div class="flex-col space-y-8 text-center px-12 max-w-2xl">
            <div class="flex-col space-y-2">
              <img src="/images/empty/tasks.svg" class="mx-auto w-36" />
              <h1 class="text-2xl font-semibold text-gray-900">
                {{ t("components.empty_states.recurring_tasks.title") }}
              </h1>
              <p class="text-gray-600">
                {{ t("components.empty_states.recurring_tasks.description") }}
              </p>
            </div>
            <primary-button
              v-if="hasPermission(permissions.createRecurringTask)"
              :label="t('admin.settings.recurring_tasks.add_task')"
              @click="handleOpenAddRecurringTasks"
            />
          </div>
        </div>
        <NoResults
          @click="clearFilters"
          v-else-if="recurringTasks.length === 0 && !isSearching && hasFilters"
        />
        <div class="flex flex-col divide-y divide-y-gray-100">
          <div
            v-for="task in recurringTasks"
            :key="task.id"
            class="flex flex-col py-4 px-4"
            @click="handleRecurringTaskClicked(task.id)"
          >
            <div class="flex items-center justify-between w-full">
              <div
                class="flex items-center space-x-0.5 font-semibold text-gray-900 text-sm"
              >
                <span>{{ task.tenant?.name }}</span>
                <chevron-right-icon class="text-gray-500 w-3 h-3" />
                <span>{{
                  task.space ? task.space.name : t("admin.tasks.no_space")
                }}</span>
              </div>
              <span class="text-sm text-gray-500">
                {{ capitalise(task.rrule.frequency) }}
              </span>
            </div>
            <div class="truncate pr-12 text-sm text-gray-700">
              <span>{{ task.description }}</span>
            </div>
          </div>
        </div>
        <div class="flex justify-center py-12" v-if="isSearching">
          <span class="flex items-center space-x-2 text-gray-400">
            <span>Loading</span>
            <loading-spinner class="h-5 w-5" />
          </span>
        </div>
      </div>
    </div>
    <detail
      :open="isTaskOpen"
      :task="lastTask"
      @task-updated="refreshTasks"
      @close="handleTaskDetailsClosed"
    />
    <new-recurring-task
      :open="openNewRecurringTask"
      @close="openNewRecurringTask = false"
      @save="handleSaveRecurringTask"
      ref="newRecurringTaskComponent"
    />
  </layout>
</template>

<script setup>
import { computed, defineProps, inject, onMounted, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import axios from "axios";
import { flatten } from "lodash";
import { useCategories } from "@/Tailwind/Mixins/useCategories";
import { storeToRefs } from "pinia";

import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import { useFilters } from "@/Tailwind/Mixins/useFilters";
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Pages/Settings";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import Category from "@/Tailwind/Components/Task/Category.vue";
import Priority from "@/Tailwind/Components/Task/Priority.vue";
import Assignee from "@/Tailwind/Components/Task/Assignee.vue";
import { capitalise } from "@/Tailwind/Helpers/text";
import Detail from "@/Tailwind/Components/Tasks/RecurringTaskDetail.vue";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import NewRecurringTask from "@/Tailwind/Components/Tasks/NewRecurringTask.vue";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import DropdownSelector from "@/Tailwind/Components/Input/DropdownSelector.vue";
import UsersFilter from "@/Tailwind/Components/Input/Filters/UsersFilter.vue";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";
import LoadingSpinner from "@/Tailwind/Components/LoadingSpinner.vue";
import SortButton from "@/Tailwind/Components/Buttons/SortButton";
import NoResults from "@/Tailwind/Components/ListView/NoResults.vue";
import HierarchySelector from "../../Components/Input/HierarchySelector.vue";
import { usePage } from "@inertiajs/vue3";
import { useFilterStore } from "@/Tailwind/Stores/filters";
import { useEventBus } from "@/Tailwind/Mixins/useEventBus";
import { usePreloadStore } from "@/Tailwind/Stores/preload";
import { useSites } from "@/Composables/useSites";

const { t } = useI18n();

const { toUTC } = useUTC();

defineOptions({ layout: PersistentLayout });

const props = defineProps({
  recurringTask: Object,
});

const { emit: emitEventBus } = useEventBus();
const clearFilters = () => emitEventBus("clearFilters");

const preloadStore = usePreloadStore();
const spaceHierarchy = computed(() => preloadStore.locations);
const assetHierarchy = computed(() => preloadStore.assets);

const { type } = useBreakpoints();
const { hasPermission, permissions } = inject("permissions");
const { data: buildings } = useSites();

const filterStore = useFilterStore();
const { recurringTaskFilters: filters } = storeToRefs(filterStore);

const openNewRecurringTask = ref(false);
const isTaskOpen = ref(!!props.recurringTask);

const recurringTasks = ref([]);
const isSearching = ref(false);

const page = ref(1);
const initialPageLoad = ref(true);
const hasMoreRecurringTasks = ref(true);

// Ensure there's a task so the <Detail /> component doesn't crash.
const lastTask = ref(props.recurringTask);
watch(
  () => props.recurringTask,
  () => {
    if (!props.recurringTask) {
      return;
    }

    lastTask.value = props.recurringTask;
  }
);

function refreshTasks() {
  reset();
  loadTasks();
}

onMounted(() => {
  loadTasks();
});

const activeTenantsForUsersFilter = computed(() => {
  return filterStore
    .activeTenants(filters.value?.spaces)
    .map((tenant) => tenant.id);
});

const loadTasksWhenBottomIsReached = ({
  target: { scrollTop, clientHeight, scrollHeight },
}) => {
  if (scrollTop + clientHeight + 100 >= scrollHeight) {
    loadTasks();
  }
};

watch(
  filters,
  () => {
    if (initialPageLoad.value) {
      return;
    }

    reset();
    loadTasks();
  },
  { deep: true }
);

function handleSearch(query) {
  filters.value.query = query;
}

function handleLocationsSelected(locations) {
  filters.value.spaces = locations;
}

function handleAssetsSelected(assets) {
  filters.value.assets = assets;
}

// Categories filter
const categories = computed(() => {
  const activeTenants = filterStore
    .activeTenants(filters.value?.spaces)
    .map((tenant) => tenant.id);

  return useCategories(activeTenants);
});

const handleCategoriesSelected = (categories) =>
  (filters.value.categories = flatten(categories));

function handleUsersSelected(users) {
  filters.value.users = users;
}

// Priorities filter
const priorities = usePage().props.priorities?.map((priority) => ({
  label: priority.name,
  value: priority.id,
}));

function handlePrioritySelected(priorities) {
  filters.value.priorities = priorities;
}

// Intervals filter
const intervals = computed(() => {
  return [
    {
      value: "daily",
      label: t("admin.tasks.recurring.interval.daily"),
    },
    {
      value: "weekly",
      label: t("admin.tasks.recurring.interval.weekly"),
    },
    {
      value: "monthly",
      label: t("admin.tasks.recurring.interval.monthly"),
    },
    {
      value: "yearly",
      label: t("admin.tasks.recurring.interval.yearly"),
    },
  ];
});

const canCreateRecurringTasks = computed(() =>
  buildings.value?.some((building) =>
    hasPermission(permissions.createRecurringTask, building.id)
  )
);

function handleIntervalSelected(intervals) {
  filters.value.intervals = intervals;
}

function reset() {
  recurringTasks.value = [];
  page.value = 1;
  hasMoreRecurringTasks.value = true;
}

const loadTasks = async () => {
  if (!hasMoreRecurringTasks.value || isSearching.value) {
    return;
  }

  isSearching.value = true;

  const { data: response } = await axios.post(
    route("api.recurring_tasks.search"),
    {
      page: page.value,
      query: filters.value?.query,
      spaces: [
        ...(filters.value.spaces || []),
        ...(filters.value.assets || []),
      ],
      categories: filters.value?.categories,
      assignees: filters.value?.users,
      priorities: filters.value?.priorities,
      intervals: filters.value?.intervals,
      per_page: 30,
      ...sort.value,
    }
  );

  recurringTasks.value = [...recurringTasks.value, ...response.data];
  hasMoreRecurringTasks.value = !!response.links.next;

  if (initialPageLoad.value) {
    initialPageLoad.value = false;
  }

  // Increment the page number to load the next batch of tasks.
  page.value += 1;

  isSearching.value = false;
};

const hasFilters = computed(() => {
  return Object.values(filters.value).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === "object") {
      return Object.values(value).some((value) => !!value);
    }

    return !!value;
  });
});

function handleOpenAddRecurringTasks() {
  openNewRecurringTask.value = true;
}

const newRecurringTaskComponent = ref();

function handleSaveRecurringTask(task) {
  router.post(
    route("settings.organisation.recurring-tasks.store"),
    {
      ...task,
      date_scheduled: task.date_scheduled
        ? toUTC(task.date_scheduled.toISOString())
        : null,
      photos: task.photos.map((photo) => photo.data),
      rrule: task.rrule.toString(),
    },
    {
      onSuccess: () => {
        openNewRecurringTask.value = false;
        newRecurringTaskComponent.value?.reset();
        refreshTasks();
      },
    }
  );
}

function handleTaskDetailsClosed() {
  router.visit(route("settings.organisation.recurring-tasks.index"), {
    preserveState: true,
    onSuccess: () => (isTaskOpen.value = false),
  });
}

function handleRecurringTaskClicked(id) {
  router.get(
    route("settings.organisation.recurring-tasks.show", [id]),
    {},
    {
      preserveState: true,
      onSuccess: (params) => {
        isTaskOpen.value = true;
        lastTask.value = params.props.recurringTask;
      },
    }
  );
}

function handleBack() {
  router.visit(route("settings.organisation.index"));
}

// Sorting
const { sort, handleSort, getSortValue } = useFilters("recurringTaskFilters");

function handleSortInner(column, direction) {
  handleSort(column, direction, () => {
    reset();
    loadTasks();
  });
}
</script>

<style lang="scss" scoped>
td {
  max-width: 16vw;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dynamic-height {
  height: calc(100vh - 151px);
}

@media (min-width: 640px) {
  .dynamic-height {
    height: calc(100vh - 64px - 145px);
  }
}
</style>
